/* eslint-disable no-console */
import { useEffect } from 'react';
import { useSpace, useLocations } from '@ably/spaces/react';
import { useRFQStore } from 'pages/auto-rfq/store';
import { rfqRowLockId } from 'lib/realtime/locks';

/**
 * Drives the "I'm looking at this row" signal. Two sources, prioritized:
 *
 *   1. `activeRfqItemId` — set by an explicit interaction (dialog open,
 *      three-dots menu, add-note modal). Strong signal; wins.
 *   2. `hoveredRfqItemId` — set by mouseenter on a row. Weak fallback; only
 *      broadcast when no explicit interaction is claiming focus.
 *
 * Broadcasts `{ rfqItemId }` as this member's Spaces location and acquires
 * the row lock (non-blocking — the in-dialog banner reads the lock state).
 *
 * Mounted once at the details-page level, not per-row.
 */
export const useRfqActiveItem = () => {
  const { space } = useSpace();
  const { update: updateLocation } = useLocations();
  const activeRfqItemId = useRFQStore(s => s.activeRfqItemId);
  const hoveredRfqItemId = useRFQStore(s => s.hoveredRfqItemId);

  useEffect(() => {
    if (!space || !updateLocation) return undefined;
    const itemId = activeRfqItemId || hoveredRfqItemId;

    // Clear the broadcast location when the user moves off every row, so the
    // inline row presence chip reflects *live* focus and cleans up when they
    // leave. The header avatar still navigates to their last row — it remembers
    // the last non-null location from the update stream (see PresenceStack).
    if (!itemId) {
      updateLocation(null).catch(err =>
        console.warn('[spaces] locations.set(null) failed', err)
      );
      return undefined;
    }

    console.info('[spaces] setting location', {
      rfqItemId: itemId,
      source: activeRfqItemId ? 'active' : 'hover'
    });
    updateLocation({ rfqItemId: itemId }).catch(err =>
      console.warn('[spaces] locations.set failed', err)
    );

    // Only acquire the row lock for "strong" interactions, not hover.
    // Hover broadcasts the halo signal but doesn't claim editing intent.
    if (activeRfqItemId) {
      const lockId = rfqRowLockId(activeRfqItemId);
      space.locks.acquire(lockId).catch(err => {
        console.info('[spaces] locks.acquire rejected', { lockId, err });
      });
      return () => {
        space.locks.release(lockId).catch(() => {
          // Expected if we never held it (acquire rejected).
        });
      };
    }

    return undefined;
  }, [space, updateLocation, activeRfqItemId, hoveredRfqItemId]);
};
